import { getPropClassesFromInstance } from 'lib/getPropClasses';
import cx from 'classnames';
import * as React from 'react';

import './styles/Columns2.scss';

/**
 * ## Two-column grid.
 *
 *
 * ```example
 * <Columns2 flex>
 *   <div>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </div>
 *   <div>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </div>
 * </Columns2>
 * ```
 *
 * ```example
 * <Columns2
 *   direction="right"
 *   gutter="xlarge">
 *   <div>
 *     First – Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </div>
 *   <div>
 *     Second – Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </div>
 * </Columns2>
 * ```
 *
 * ```example
 * <Columns2 reset="tablet">
 *   <div>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </div>
 *   <div>
 *     Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt
 *     ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
 *     ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in
 *     reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
 *   </div>
 * </Columns2>
 * ```
 *
 *
 * # TODO
 * * Fully deprecate [Columns](/internal/components/Columns)
 * * Use flexbox
 * * All column sizes
 * * Different column sizes
 */

type Props = {
  flex?: boolean;
  reset?: 'tablet' | 'mobile';
  resetFlush?: boolean;
  className?: string;
  divider?: boolean;
} & DefaultProps;

type DefaultProps = {
  direction: 'right' | 'left';
  gutter:
    | 'xxxxxxlarge'
    | 'xxxxxlarge'
    | 'xxxxlarge'
    | 'xxxlarge'
    | 'xxlarge'
    | 'xlarge'
    | 'large'
    | 'medium'
    | 'small'
    | 'xsmall'
    | 'xxsmall'
    | 'xxxsmall'
    | 'xxxxsmall';
};

const mapPropClasses = (
  items: string[],
  vfn = (item: string) => item,
  kfn = (item: string) => item
) => items.reduce((acc, i) => ({ ...acc, [kfn(i)]: vfn(i) }), {});

class Columns2 extends React.Component<Props> {
  static propClasses = {
    direction: {
      left: 'Columns2--rev',
    },
    gutter: mapPropClasses(
      [
        'xxxxxxlarge',
        'xxxxxlarge',
        'xxxxlarge',
        'xxxlarge',
        'xxlarge',
        'xlarge',
        'large',
        'medium',
        'small',
        'xsmall',
        'xxsmall',
        'xxxsmall',
        'xxxxsmall',
      ],
      (x) => `Columns2--${x}`
    ),
    reset: mapPropClasses(['tablet', 'mobile'], (x) => `Columns2--reset--${x}`),
    resetFlush: 'Columns2--reset--flush',
    flex: 'Columns2--flex',
  };

  static defaultProps: DefaultProps = {
    direction: 'right',
    gutter: 'xxxxlarge',
  };

  wrap = (node: React.ReactNode) => {
    return <div className="Columns2-column Columns2-column1-2">{node}</div>;
  };

  render() {
    const { children, className, divider } = this.props;
    const body = React.Children.map(children, function (child, index) {
      return (
        <div
          key={index}
          className={cx('Columns2-column', 'Columns2-column1-2', {
            'Columns2--divider': divider && index === 0,
          })}
        >
          {child}
        </div>
      );
    });
    return (
      <div className={cx('Columns2', getPropClassesFromInstance(this), className)}>{body}</div>
    );
  }
}
export default Columns2;
